// IMPORTANT: Need to allow 3rd party cookies from login.microsoft.com for token renewal
// TODO: https://docs.microsoft.com/en-gb/azure/active-directory/develop/tutorial-v2-angular-auth-code

import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { environment } from '@env/environment';
import { OKTA_AUTH } from '@okta/okta-angular';
import {
  ESQEP_AUTH_SAML,
  ESQEP_AUTH_TYPE,
} from 'app/core/constants/constants.component';
import { LoggedInUser } from '@core/models/person/logged-in-user.model';
import { SecurityRole } from '@core/models/security/security-role';
import { SECURITY_ROLE_KEY } from '@core/models/security/security-role-keys';
import { BrowserStorageService } from '@core/utilities/browser-storage/browser-storage.service';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
  throwError,
} from 'rxjs';
import { ConfigService } from '../config/config.service';
import { AUTHENTICATION_CONFIGURATION_NAMES } from './authenticationConfigNames';
import { UserPermission } from '@core/models/security/user-permission';
import { IntegrationTypes } from '@core/models/types/integration-types';
import { PermissionTypes } from '@core/models/types/permission-types';
import { REDIRECT_ON_403 } from '@core/interceptors/auth-403.context';

export const SecurityRoleConfig = {};

const dotNetServerUri = environment.serverUrl;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private _userSecurityRoleLookup: { [role_identifier: string]: SecurityRole } =
    {};
  private _userSecurityRoles: Array<SecurityRole> = [];
  private _userPermissions: Array<UserPermission> = [];
  private ngUnsubscribe: Subject<boolean> = new Subject();
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly oktaAuth = inject(OKTA_AUTH, { optional: true });
  private readonly browserStorage = inject(BrowserStorageService);
  public readonly msalService = inject(MsalService, { optional: true });

  diagShow = false;
  authenticationStatusSubj = new BehaviorSubject<boolean>(false);
  adminStatusSubj = new BehaviorSubject<boolean>(false);
  inUserGroupSubj = new BehaviorSubject<boolean>(false);
  loggedInUserSubj = new BehaviorSubject<LoggedInUser | null>(null);
  authTimeoutSubj = new Subject<any>();
  authServiceSubs = new Subscription();
  userPhotoUrlSubj = new BehaviorSubject<SafeResourceUrl>('');
  permissionsSubject = new ReplaySubject<Array<UserPermission>>(1);
  securityRolesSubj = new ReplaySubject<Array<SecurityRole>>(1);
  securityRoleLookupSubj = new ReplaySubject<any>(1);
  userInfo: LoggedInUser;

  ngOnDestroy() {
    this.authServiceSubs.unsubscribe();
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  loginSucceeded() {
    this.isAuthenticated = true;
    this.inUserGroup = true;
    this.hasAdminRole =
      this.userSecurityRoles.findIndex(
        (f) => f.roleIdentifier === SECURITY_ROLE_KEY.Admin,
      ) > -1;
  }

  logout() {
    if (
      this.browserStorage.get(ESQEP_AUTH_TYPE) ===
      AUTHENTICATION_CONFIGURATION_NAMES.CSOD
    ) {
      this.isAuthenticated = false;
      this.loggedInUserSubj.next(null);
      this.userInfo = null;
      this.storeUserPersonDetail(null);
      this.browserStorage.remove(ESQEP_AUTH_SAML);
      this.browserStorage.remove(ESQEP_AUTH_TYPE);
      window.location.href =
        this.configService.authSetup[
          AUTHENTICATION_CONFIGURATION_NAMES.CSOD
        ].logoutUrl;
    } else if (
      this.browserStorage.get(ESQEP_AUTH_TYPE) ===
      AUTHENTICATION_CONFIGURATION_NAMES.OKTA
    ) {
      this.browserStorage.remove(ESQEP_AUTH_TYPE);
      of(this.oktaAuth.signOut()).subscribe(() =>
        this.router.navigate(['/login']),
      );
    } else {
      this.browserStorage.remove(ESQEP_AUTH_TYPE);
      this.msalService?.logoutRedirect();
    }
  }

  timeoutLogout() {
    this.logout();
  }

  storeUserPersonDetail(person: LoggedInUser) {
    if (person) {
      person.knownAsName = person.knownAsName || person.givenName;
      this.loggedInUserSubj.next(person);
    } else {
      this.loggedInUserSubj.next(null);
    }
  }

  getSecurityRoles(): Observable<Array<SecurityRole>> {
    return this.http.get<Array<SecurityRole>>(
      `${dotNetServerUri}/permissions/integration`,
    );
  }

  populateSecurityRoles() {
    this.authServiceSubs.add(
      this.getSecurityRoles().subscribe({
        next: (sr) => {
          this._userSecurityRoles = sr;
          this.securityRolesSubj.next(sr);
          this._userSecurityRoleLookup = sr.reduce((m, r) => {
            (m as any)[r.roleIdentifier] = r;
            return m;
          }, {} as { [role_identifier: string]: SecurityRole });
          this.securityRoleLookupSubj.next(this._userSecurityRoleLookup);
        },
        error: () => {},
      }),
    );
  }

  getUserPermissions(): Observable<Array<UserPermission>> {
    return this.http.get<Array<UserPermission>>(
      `${dotNetServerUri}/permissions/user`,
    );
  }

  userHasPermission(integrationId: number, permissionType: number): boolean {
    return this._userPermissions.some(
      (x) => x.integrationId === integrationId && x.permissionType == permissionType,
    );
  }

  userHasConnectPermission(): boolean {
    return this.userHasPermission(IntegrationTypes.SqeptechConnect, PermissionTypes.View);
  }

  populateUserPermissions() {
    this.authServiceSubs.add(
      this.getUserPermissions().subscribe({
        next: (x) => {
          this._userPermissions = x;
          this.permissionsSubject.next(x);
        },
        error: () => {},
      }),
    );
  }

  get userSecurityRoles(): Array<SecurityRole> {
    return this._userSecurityRoles;
  }

  get me(): LoggedInUser {
    return this.loggedInUserSubj.getValue() as LoggedInUser;
  }

  get myPhotoUrl(): SafeResourceUrl {
    return this.userPhotoUrlSubj.getValue();
  }

  public get isAuthenticated() {
    return this.authenticationStatusSubj.getValue();
  }

  public set isAuthenticated(value: boolean) {
    this.authenticationStatusSubj.next(value);
  }

  public get inUserGroup(): boolean {
    return this.inUserGroupSubj.getValue();
  }

  public set inUserGroup(value: boolean) {
    if (this.inUserGroupSubj.getValue() !== value) {
      this.inUserGroupSubj.next(value);
    }
  }

  public get hasAdminRole(): boolean {
    return this.adminStatusSubj.getValue();
  }

  public set hasAdminRole(value: boolean) {
    if (this.adminStatusSubj.getValue() !== value) {
      this.adminStatusSubj.next(value);
    }
  }

  get userGUID(): string {
    return this.userInfo?.externalGuid || this.userInfo?.adObjectGuid;
  }

  public get userUPN(): string {
    return this.userInfo?.adUpn as string;
  }

  public getSsoRedirect() {
    const returnUrl = window.location.origin + '/home';
    const spConfigName =
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.CSOD]
        .spConfigName;
    return this.http
      .get(
        `${dotNetServerUri}/auth/${spConfigName}?returnUrl=${returnUrl}&redirectUrl=${returnUrl}`,
      )
      .pipe(
        map((resp) => {
          return (resp as any) || {};
        }),
        catchError((err) => {
          return throwError(() => new Error(err));
        }),
      );
  }
  
  public getCurrentCustomer(): Observable<any> {
    return this.http.get(`${dotNetServerUri}/auth/current-customer`, {
      context: new HttpContext().set(REDIRECT_ON_403, true),
    });
  }

  public getMeEndpoint(): Observable<any> {
    return this.http.get(`${dotNetServerUri}/auth/me`, {
      context: new HttpContext().set(REDIRECT_ON_403, true),
    });
  }

  public getLoggedInUser(): Observable<LoggedInUser> {
    return this.http.get<LoggedInUser>(
      `${dotNetServerUri}/people/loggedInUser`,
      {
        context: new HttpContext().set(REDIRECT_ON_403, true),
      },
    );
  }

  public storeLoggedInUserFromApi(): Observable<LoggedInUser> {
    return this.getLoggedInUser().pipe(
      map((u) => {
        this.userInfo = u;
        this.storeUserPersonDetail(u);
        return u;
      })
    );
  }
}